(function initialiseYflSubscriptionCartGuard(global) {
  'use strict';

  const protectedProductHandles = new Set(['novos-core', 'novos-boost', 'novos-vital']);

  function normaliseProductHandle(item) {
    const explicitHandle = item?.handle || item?.product_handle || '';
    if (explicitHandle) return String(explicitHandle).toLowerCase();

    const url = String(item?.url || '');
    const match = url.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  }

  function inferRequiredQuantity(item, configuredPlanQuantities = {}) {
    const allocation = item?.selling_plan_allocation;
    if (!allocation || !protectedProductHandles.has(normaliseProductHandle(item))) return 0;

    const sellingPlan = allocation.selling_plan || {};
    const configuredQuantity = Number(configuredPlanQuantities[String(sellingPlan.id)] || 0);
    if (configuredQuantity === 6 || configuredQuantity === 12) return configuredQuantity;

    const planName = String(sellingPlan.name || '').toLowerCase();
    if (/(^|\D)6(\D|$)/.test(planName) || planName.includes('semi-annually')) return 6;
    if (/(^|\D)12(\D|$)/.test(planName) || planName.includes('annually')) return 12;
    return 0;
  }

  function findInvalidLines(cart, configuredPlanQuantities = {}) {
    return (cart?.items || []).flatMap((item, index) => {
      const requiredQuantity = inferRequiredQuantity(item, configuredPlanQuantities);
      if (!requiredQuantity || Number(item.quantity) === requiredQuantity) return [];
      return [{ item, line: index + 1, requiredQuantity }];
    });
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { findInvalidLines, inferRequiredQuantity, normaliseProductHandle };
  }

  if (!global.document) return;

  const document = global.document;
  const script = document.getElementById('YflSubscriptionCartGuardScript');
  if (!script) return;

  function parsePlanQuantities(value) {
    try {
      const parsed = JSON.parse(value || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
      return {};
    }
  }

  const configuredPlanQuantities = {
    ...parsePlanQuantities(script.dataset.planQuantities),
    ...parsePlanQuantities(document.getElementById('YflSubscriptionClarityScript')?.dataset.planQuantities),
  };
  const cartUrl = script.dataset.cartUrl || `${global.Shopify?.routes?.root || '/'}cart.js`;
  const cartChangeUrl = script.dataset.cartChangeUrl || global.routes?.cart_change_url || '/cart/change.js';
  const lockedTemplate = script.dataset.lockedTemplate || 'This prepaid plan contains [quantity] packs shipped together.';
  const correctedMessage = script.dataset.corrected || 'The complete prepaid shipment has been restored. Review the updated cart total before checkout.';
  const errorMessage = script.dataset.error || 'We could not verify the prepaid subscription quantity. Refresh the cart or remove the plan before checkout.';
  const correctedStorageKey = 'yfl-subscription-cart-corrected';
  const state = {
    allowedForm: null,
    blocked: false,
    busy: false,
    validationTimer: null,
  };

  function formatLockedMessage(quantity) {
    return lockedTemplate.replace('[quantity]', String(quantity));
  }

  function renderedBulkLines() {
    return [...document.querySelectorAll('[data-yfl-required-quantity][data-yfl-cart-key]')];
  }

  function checkoutButtons() {
    return [...document.querySelectorAll('button[name="checkout"], input[name="checkout"], #checkout, #CartDrawer-Checkout')];
  }

  function updateCheckoutState() {
    const shouldDisable = state.busy || state.blocked;
    checkoutButtons().forEach((button) => {
      if (!button.dataset.yflOriginalDisabled) {
        button.dataset.yflOriginalDisabled = button.disabled ? 'true' : 'false';
      }

      if (shouldDisable) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.yflOriginalDisabled !== 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    });
  }

  function setBusy(busy) {
    state.busy = busy;
    document.documentElement.classList.toggle('yfl-subscription-cart-validating', busy);
    updateCheckoutState();
  }

  function announce(message, isError = false) {
    const status = document.getElementById('YflSubscriptionCartGuardStatus');
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
    status.classList.toggle('yfl-subscription-cart-guard-status--error', isError);
    status.setAttribute('role', isError ? 'alert' : 'status');
  }

  function lockRenderedLines() {
    renderedBulkLines().forEach((line) => {
      const requiredQuantity = Number(line.dataset.yflRequiredQuantity || 0);
      if (!requiredQuantity) return;

      const input = line.querySelector('input[name="updates[]"]');
      if (input) {
        input.min = String(requiredQuantity);
        input.max = String(requiredQuantity);
        input.step = '1';
        input.readOnly = true;
        input.dataset.yflRequiredQuantity = String(requiredQuantity);
      }

      line.querySelectorAll('quantity-input .quantity__button').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });

      const note = line.querySelector('[data-yfl-bulk-status]');
      if (note) note.textContent = formatLockedMessage(requiredQuantity);
    });
  }

  async function fetchCart() {
    const response = await global.fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
    return response.json();
  }

  async function setLineQuantity(line, requiredQuantity) {
    const response = await global.fetch(cartChangeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ line, quantity: requiredQuantity }),
    });
    if (!response.ok) throw new Error(`Cart correction failed with ${response.status}`);
    return response.json();
  }

  async function correctInvalidLines(invalidLines) {
    for (const invalidLine of invalidLines) {
      await setLineQuantity(invalidLine.line, invalidLine.requiredQuantity);
    }

    try {
      global.sessionStorage.setItem(correctedStorageKey, 'true');
    } catch (_error) {
      // The visible cart state is still refreshed even when session storage is unavailable.
    }
    global.location.reload();
  }

  async function validateCart({ correct = true } = {}) {
    if (state.busy) return { valid: false, pending: true };
    setBusy(true);

    try {
      const cart = await fetchCart();
      const invalidLines = findInvalidLines(cart, configuredPlanQuantities);
      if (invalidLines.length && correct) {
        state.blocked = true;
        updateCheckoutState();
        await correctInvalidLines(invalidLines);
        return { valid: false, corrected: true };
      }

      state.blocked = invalidLines.length > 0;
      updateCheckoutState();
      return { valid: invalidLines.length === 0, invalidLines };
    } catch (error) {
      const knownBulkCart = renderedBulkLines().length > 0 || Object.keys(configuredPlanQuantities).length > 0;
      state.blocked = knownBulkCart;
      updateCheckoutState();
      if (knownBulkCart) announce(errorMessage, true);
      return { valid: !knownBulkCart, error };
    } finally {
      setBusy(false);
    }
  }

  function submitValidatedForm(form, submitter) {
    state.allowedForm = form;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
      return;
    }

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = '1';
    form.appendChild(checkoutInput);
    HTMLFormElement.prototype.submit.call(form);
  }

  async function guardCheckout(submitter) {
    if (state.busy) return;
    const form = submitter?.form;
    if (!form) return;

    const result = await validateCart({ correct: true });
    if (result.valid) submitValidatedForm(form, submitter);
  }

  function scheduleValidation() {
    global.clearTimeout(state.validationTimer);
    state.validationTimer = global.setTimeout(() => {
      lockRenderedLines();
      if (renderedBulkLines().length) validateCart({ correct: true });
    }, 100);
  }

  document.addEventListener(
    'click',
    (event) => {
      const submitter = event.target.closest?.('button[name="checkout"], input[name="checkout"], #checkout, #CartDrawer-Checkout');
      if (!submitter || submitter.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      guardCheckout(submitter);
    },
    true,
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (state.allowedForm === event.target) {
        state.allowedForm = null;
        return;
      }

      const submitter = event.submitter;
      if (!submitter?.matches?.('[name="checkout"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      guardCheckout(submitter);
    },
    true,
  );

  document.addEventListener('cartQuantityUpdated', scheduleValidation);

  const observer = new MutationObserver((mutations) => {
    const hasRelevantChange = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('[data-yfl-required-quantity]') || node.querySelector?.('[data-yfl-required-quantity]')),
      ),
    );
    if (hasRelevantChange) scheduleValidation();
  });

  function initialise() {
    lockRenderedLines();
    observer.observe(document.body, { childList: true, subtree: true });

    try {
      if (global.sessionStorage.getItem(correctedStorageKey) === 'true') {
        global.sessionStorage.removeItem(correctedStorageKey);
        announce(correctedMessage);
      }
    } catch (_error) {
      // Session storage is optional; the cart itself remains protected.
    }

    if (renderedBulkLines().length) validateCart({ correct: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})(typeof window !== 'undefined' ? window : globalThis);
